import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { fetchJson } from './adminApi';
import { fmt, kr, smallImage, typeLabel } from './adminFormat';
import { getListingKey } from '../map/listingDetailsApi.js';

// One listing: every stored field, all photos and floor plans (arrow keys browse), broker logo, links.
const ListingDetailDialog = ({ listingUrl, onClose }) => {
    const dialogRef = useRef(null);
    const stripRef = useRef(null);
    const [detail, setDetail] = useState(null);
    const [error, setError] = useState('');
    const [set, setSet] = useState('images');
    const [index, setIndex] = useState(0);
    const [stats, setStats] = useState(null);

    // Per-listing engagement (opens / favourites / realtor clicks), keyed by listing_id — the same key this
    // listing's events carry. One all-time call, server-cached; look this listing up in the map.
    useEffect(() => {
        let active = true;
        fetchJson('/admin/analytics/listings')
            .then((payload) => active && setStats(payload || {}))
            .catch(() => {});
        return () => {
            active = false;
        };
    }, []);
    const stat = stats
        ? (stats[getListingKey({ listingUrl: `/${String(listingUrl || '').replace(/^\/+/, '')}` })] || {})
        : null;

    useEffect(() => {
        if (!listingUrl) return undefined;
        let active = true;
        setDetail(null);
        setError('');
        setSet('images');
        setIndex(0);
        if (!dialogRef.current?.open) dialogRef.current?.showModal();
        fetchJson(`/admin/explorer/listing?url=${encodeURIComponent(listingUrl)}`)
            .then((payload) => active && setDetail(payload))
            .catch((err) => active && setError(err.message));
        return () => {
            active = false;
        };
    }, [listingUrl]);

    const list = useMemo(
        () => (detail ? (set === 'images' ? detail.images : detail.floorplans) || [] : []),
        [detail, set],
    );
    const show = useCallback((next) => setIndex(Math.max(0, Math.min(next, list.length - 1))), [list.length]);

    useEffect(() => {
        const handleKey = (event) => {
            if (!dialogRef.current?.open) return;
            if (event.key === 'ArrowLeft') show(index - 1);
            if (event.key === 'ArrowRight') show(index + 1);
        };
        document.addEventListener('keydown', handleKey);
        return () => document.removeEventListener('keydown', handleKey);
    }, [index, show]);

    useEffect(() => {
        // Preload the neighbours and keep the active thumbnail in view.
        [index - 1, index + 1].forEach((n) => {
            if (list[n]) new Image().src = list[n];
        });
        stripRef.current?.querySelector('.is-active')?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    }, [index, list]);

    const close = () => {
        dialogRef.current?.close();
    };

    const a = detail?.listing || {};
    const e = detail?.extra || {};
    const facts = [
        ['Price', kr(a.price)], ['Price / m²', kr(a.sqmPrice)], ['Estimate', kr(e.estimatePrice)],
        ['Estimate range', e.estimateLow ? `${kr(e.estimateLow)} – ${kr(e.estimateHigh)}` : '—'],
        ['First price', kr(e.firstPrice)], ['Price change', e.listPriceChange ? kr(e.listPriceChange) : '—'],
        ['Rooms', fmt(a.rooms || '')], ['Living area', a.livingM2 ? `${fmt(a.livingM2)} m²` : '—'],
        ['Additional area', e.additionalArea ? `${fmt(e.additionalArea)} m²` : '—'],
        ['Plot', a.plot_area ? `${fmt(a.plot_area)} m²` : '—'],
        ['Fee', a.fee ? `${fmt(a.fee)} kr/mån` : '—'],
        ['Floor', e.floor ?? '—'], ['Built', e.constructionYear || '—'], ['Tenure', e.tenureForm || '—'],
        ['BRF', e.housingCoopName || '—'], ['Energy class', e.energyClass || '—'],
        ['Balcony / patio', [e.hasBalcony ? 'Balcony' : '', e.patio || ''].filter(Boolean).join(', ') || '—'],
        ['Amenities', e.amenities ? String(e.amenities).split(',').join(', ') : '—'],
        ['Status', a.imgTag || '—'], ['Published', a.date || '—'], ['Days listed', e.daysActive ?? '—'],
        ['Agency', e.sourceName || '—'], ['RegSO', a.regso_id || '—'], ['Coordinates', a.latlong || '—'],
        ['Last checked', a.lastChecked || '—'],
    ];

    return (
        <dialog
            ref={dialogRef}
            className="ax-detail"
            aria-labelledby="ax-detail-title"
            onClose={onClose}
            onClick={(event) => event.target === dialogRef.current && close()}
        >
            <div className="ax-detail-head">
                <div>
                    <h2 id="ax-detail-title">{a.address || listingUrl}</h2>
                    <p className="ax-subtitle">{[a.ort, a.kommun, a.type ? typeLabel(a.type) : ''].filter(Boolean).join(' · ')}</p>
                    {stat && (
                        <p className="ax-detail-stats" title="From the site's analytics, all of time">
                            <span><strong>{fmt(stat.opens || 0)}</strong> opens{stat.opens_visitors ? ` · ${fmt(stat.opens_visitors)} unique` : ''}</span>
                            <span><strong>{fmt(stat.favorites || 0)}</strong> favourites</span>
                            <span><strong>{fmt(stat.realtor || 0)}</strong> realtor clicks{stat.realtor_visitors ? ` · ${fmt(stat.realtor_visitors)} unique` : ''}</span>
                        </p>
                    )}
                </div>
                <button type="button" className="ax-icon-btn" onClick={close} aria-label="Close">✕</button>
            </div>
            {error && <p className="ax-error ax-detail-pad">{error}</p>}
            {!detail && !error && <p className="ax-muted ax-detail-pad">Loading…</p>}
            {detail && (
                <>
                    <div className="ax-gallery">
                        <div className="ax-gallery-tabs" role="tablist">
                            <button type="button" role="tab" aria-selected={set === 'images'} onClick={() => { setSet('images'); setIndex(0); }}>
                                Photos ({detail.images.length})
                            </button>
                            <button
                                type="button"
                                role="tab"
                                aria-selected={set === 'floorplans'}
                                disabled={!detail.floorplans.length}
                                onClick={() => { setSet('floorplans'); setIndex(0); }}
                            >
                                Floor plans ({detail.floorplans.length})
                            </button>
                        </div>
                        <div className="ax-stage">
                            {list.length ? (
                                <>
                                    <button type="button" className="ax-nav ax-nav--prev" onClick={() => show(index - 1)} disabled={index <= 0} aria-label="Previous image">‹</button>
                                    <img src={list[index]} alt={`${set === 'images' ? 'Photo' : 'Floor plan'} ${index + 1}`} />
                                    <button type="button" className="ax-nav ax-nav--next" onClick={() => show(index + 1)} disabled={index >= list.length - 1} aria-label="Next image">›</button>
                                    <span className="ax-counter">{index + 1} / {list.length}</span>
                                </>
                            ) : (
                                <p className="ax-no-images">No images for this listing.</p>
                            )}
                        </div>
                        <div className="ax-strip" ref={stripRef}>
                            {list.map((url, i) => (
                                <button
                                    key={url}
                                    type="button"
                                    className={i === index ? 'is-active' : ''}
                                    onClick={() => setIndex(i)}
                                    aria-label={`Show image ${i + 1}`}
                                >
                                    <img loading="lazy" src={smallImage(url)} alt="" />
                                </button>
                            ))}
                        </div>
                    </div>
                    <div className="ax-detail-body">
                        <dl className="ax-kv">
                            {facts.map(([label, value]) => (
                                <React.Fragment key={label}>
                                    <dt>{label}</dt>
                                    <dd>{String(value)}</dd>
                                </React.Fragment>
                            ))}
                        </dl>
                        <div className="ax-detail-links">
                            {detail.broker_logo && <img src={detail.broker_logo} alt="Broker logo" />}
                            {a.realtorUrl && (
                                <a className="ax-button ax-button--secondary" href={a.realtorUrl} target="_blank" rel="noopener noreferrer">Open realtor page</a>
                            )}
                        </div>
                    </div>
                </>
            )}
        </dialog>
    );
};

export default ListingDetailDialog;
